//third party
import { useAtom } from "jotai";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useLoaderData, useLocation } from "react-router-dom";
import { Alert, Card } from "react-bootstrap";
import { Asterisk, At } from "react-bootstrap-icons";
//spotsync
import { Button } from "./Controls/Button";
import { authAtom, redirectAtom, authTokenAtom } from "./Data/Atoms";
import { useState } from "react";
import { HeaderText } from "./Controls/HeaderText";
import { CenteredPage } from "./Controls/CenteredPage";
import { Input } from "./Controls/Input";
import { AuthApi, ParkerApi } from "./shared/api/axios-client";
import { createApiService } from "./shared/apiConfig";
import { Operator } from "./Data/ApiTypes";
import { cents, showMoney } from "./Data/Money";
import SubscriptionIntroModal from "./SubscriptionIntroModal";

export const LogInPage = () => {
    const [auth, setAuth] = useAtom(authAtom);
    const [authToken, setAuthToken] = useAtom(authTokenAtom);
    const [errMsg, setErrMsg] = useState<string>();
    const [redirect, setRedirect] = useAtom(redirectAtom);
    const [isLoading, setIsLoading] = useState(false);
    // Only show modal if redirect URL contains planId or invite parameter
    const shouldShowModal = redirect !== undefined && (() => {
        try {
            const url = new URL(redirect, window.location.origin);
            return url.searchParams.has('planId') || url.searchParams.has('invite');
        } catch {
            return false;
        }
    })();
    const [showIntroModal, setShowIntroModal] = useState(shouldShowModal);
    
    const authApi = createApiService(AuthApi);
    const parkerApi = createApiService(ParkerApi);
    const op = useLoaderData() as Operator;
    const nav = useNavigate();
    const location = useLocation();
    const prefilledEmail = (location.state as any)?.email || "";        
    const inviteData = (location.state as any)?.inviteData;   

    interface LogInForm {
        emailAddress: string;
        password: string;
    }

    const { register, handleSubmit, formState: { errors }, setValue } = useForm<LogInForm>({
        defaultValues: {
            emailAddress: prefilledEmail,
            password: ""
        }
    });

    function submit(data: LogInForm) {
        setIsLoading(true);
        const username = `${data.emailAddress}`;
        authApi.login({
            serverServicesIntegrationsAccountLoginRequestModel: {
                userName: username,
                password: data.password
            }
        }, {
            params: {
                hasToken: false
            }
        }).then(
            res => {
                if (res.data.userNotFound == false) {

                    const parkerId = res.data.spotSyncUserId!
                    setAuthToken({ accessToken: res.data.accessToken! });
                    parkerApi.getParker({
                        id: parkerId
                    }).then((parkerResponse) => {
                        setAuth({ isLoggedIn: true, account: parkerResponse.data.item as any })
                        if (redirect !== undefined) {
                            setRedirect(undefined);
                            nav(`${redirect}`);
                        } else {
                            nav("/");
                        }
                    })
                } else {
                    setErrMsg("User not found");
                }
            }).catch((err) =>
                setErrMsg(err.response?.data?.message || "An error occurred while logging in.")

            ).finally(() => setIsLoading(false))
    }

    return <>
        <CenteredPage sm="10" md="8" lg="6" xl="4" style={{ maxWidth: "30em" }}>
       
        <div className="d-flex flex-column align-items-center w-100">
        {op.logoUrl && (
        <div className="text-center mb-4">
            <img
            width={150}
            style={{ maxHeight: "150px" }}
            src={op.logoUrl}
            alt={`${op.name} Logo`}
            className="m-4"
            />
        </div>
        )}
          
          <Card className="w-100">
            <form onSubmit={handleSubmit(submit)}>
                <Card.Header>
                    <HeaderText>Log In</HeaderText>
                </Card.Header>
                <Card.Body className="py-4">
                    <Input
                        type="email"
                        label="Email Address"
                        icon={<At className="fs-5" />}
                        placeholder="Enter your email"
                        error={errors.emailAddress}
                        {...register("emailAddress", { required: "Email Address is required" })}
                    />
                    <Input
                        type="password"
                        label="Password"
                        icon={<Asterisk className="fs-6" />}
                        placeholder="Enter your password"
                        error={errors.password}
                        {...register("password", { required: "Password is required" })}
                    />
                    {errMsg && (
                        <Alert variant="danger" className="text-danger">
                            {errMsg}
                        </Alert>
                    )}
                    <div className="mb-3">
                        <Link to="/signup">Sign up</Link>
                    </div>
                    <div>
                        <Link to="/forgot">Forgot Password</Link>
                    </div>
                </Card.Body>
                <Card.Footer>
                    <div>
                        {redirect !== undefined && <Alert>
                            You will be redirected after you log-in.
                        </Alert>}
                    </div>
                    <div className="d-flex flex-row justify-content-end gap-2">
                        <Button className="w-100" onClick={() => nav("/")}>
                            Back
                        </Button>
                        <Button type="submit"
                            loading={isLoading}
                            loadingText="Loading"
                            className="w-100 btn-main">
                            Log In
                        </Button>
                    </div>
                </Card.Footer>
            </form>
        </Card>
        </div>
    </CenteredPage>

    {inviteData && (
      <SubscriptionIntroModal
        show={showIntroModal}
        setShow={setShowIntroModal}
        inviteData={inviteData}
        showMoney={showMoney}
        cents={cents}
      />
    )}
    </>;
};
